import {
	ChangeDetectionStrategy,
	Component,
	HostBinding,
	OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BrokerageType, MatterFileType } from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ButtonGroupOption } from 'src/app/shared/atomic/atoms/button-group/button-group.component';
import { TestHooks } from './commission-brokerage-selection.hooks';
import { MatterDispatch } from '@state/matter';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { SITE_SECTION } from '@state/navigation/page.model';

const SubpathMapping = {
	[BrokerageType.BUYER_BROKERAGE]: 'buyer-brokerage',
	[BrokerageType.SELLER_BROKERAGE]: 'seller-brokerage',
};

@UntilDestroy()
@Component({
	selector: 'app-commission-brokerage-selection',
	templateUrl: './commission-brokerage-selection.component.html',
	styleUrls: ['./commission-brokerage-selection.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class CommissionBrokerageSelectionComponent implements OnInit {
	testHooks = TestHooks;
	public brokerageSelectionOptions: ButtonGroupOption<BrokerageType>[] = [
		{
			label: 'Seller Brokerage',
			value: BrokerageType.SELLER_BROKERAGE,
		},
		{
			label: 'Buyer Brokerage',
			value: BrokerageType.BUYER_BROKERAGE,
			intercomAttr: 'commissions-select-buyer-brokerage',
		},
	];
	public mappedBrokerageSelectionOptions$: Observable<
		ButtonGroupOption<BrokerageType>[]
	> = this.store
		.select(SelectedMatter.showBuyerBrokerage)
		.pipe(
			map((showBuyerBrokerage) =>
				showBuyerBrokerage
					? this.brokerageSelectionOptions
					: this.brokerageSelectionOptions.filter(
							(option) => option.value !== BrokerageType.BUYER_BROKERAGE,
						),
			),
		);

	public matterFileType = MatterFileType;
	public currentBrokerageSelection$ = new BehaviorSubject(
		BrokerageType.BUYER_BROKERAGE,
	);
	public isInFileCreationFlow = false;

	@Select(SelectedMatter.matterFileType) fileType$: Observable<MatterFileType>;
	@Select(SelectedMatter.showBuyerBrokerage)
	showBuyerBrokerage$: Observable<boolean>;
	@ViewSelectSnapshot(SelectedMatter.id) matterId: string;

	@HostBinding('class.bg-gray-50') bgGray = false;

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private analytics: AnalyticsService,
		private store: Store,
		private matterDispatch: MatterDispatch,
	) {}

	ngOnInit() {
		combineLatest([this.route.data, this.fileType$])
			.pipe(untilDestroyed(this))
			.subscribe(([routeData, fileType]) => {
				// If matter file type is PURCHASE, user can only see Buyer Brokerage Commission page
				if (fileType === MatterFileType.PURCHASE) {
					this.currentBrokerageSelection$.next(BrokerageType.BUYER_BROKERAGE);
					return;
				}

				const selectedPage: BrokerageType = routeData?.page;
				if (selectedPage) {
					this.currentBrokerageSelection$.next(selectedPage);
				}
			});

		this.route.data.pipe(untilDestroyed(this)).subscribe((routeData) => {
			this.isInFileCreationFlow = Object.prototype.hasOwnProperty.call(
				routeData,
				'step',
			);

			this.bgGray = routeData.section === SITE_SECTION.MatterCreation;
		});
	}

	onBrokerageSelection(selectedBrokerage: BrokerageType) {
		this.fileType$.pipe(untilDestroyed(this), take(1)).subscribe((fileType) => {
			// Tracking the times that the Buyer Brokerage tab is selected in file creation flow and regular file workflow in sale files
			if (
				fileType === MatterFileType.SALE &&
				selectedBrokerage === BrokerageType.BUYER_BROKERAGE
			) {
				const analyticsEvent = this.isInFileCreationFlow
					? AnalyticsEvent.SALE_FILE_CREATION_FLOW_COMMISSIONS_BUYER_BROKERAGE_TAB_CLICK
					: AnalyticsEvent.SALE_FILE_REG_WORKFLOW_COMMISSIONS_BUYER_BROKERAGE_TAB_CLICK;

				this.analytics.logEvent(analyticsEvent);
			}

			// If matter file type is PURCHASE, user can only see Buyer Brokerage Commission page
			const subpath =
				fileType === MatterFileType.PURCHASE
					? SubpathMapping[BrokerageType.BUYER_BROKERAGE]
					: SubpathMapping[selectedBrokerage];

			this.router.navigate([subpath], { relativeTo: this.route.parent });
		});
	}

	toggleBuyerBrokerageCommission($event: boolean) {
		this.matterDispatch.OverwriteFields('showBuyerBrokerage', $event);
	}
}
